import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import {
  Search,
  MapPin,
  SlidersHorizontal,
  X,
  LayoutGrid,
  List,
  ChevronLeft,
  ChevronRight,
  Package,
  Image,
} from 'lucide-react';
import { useGetFirestoreItemsByQuery, useDebounce } from '../hooks';
import {
  locationMessages,
  categoryMessages,
  LOCATIONS,
  COLLECTIONS_QUERIES,
  CATEGORIES,
  LISTING_ITEM_STATUSES,
} from '../constants';
import {
  getQueryFilter,
  getFormattedCategories,
  getFormattedLocations,
  getFormattedSortOptions,
} from '../utils';
import { Filter } from '../api/getFirestoreItemByQuery.ts';
import { LayoutWrapper } from '../components/LayoutWrapper.tsx';
import { Select } from '../components/Select.tsx';
import { Service } from '../types/index.ts';

const ITEMS_PER_PAGE_OPTIONS = [6, 12, 24, 48];
const DEFAULT_SORT = 'byRelevant';

const getFormattedItemsPerPageOptions = () => {
  return ITEMS_PER_PAGE_OPTIONS.map((option) => ({
    label: option,
    value: option.toString(),
  }));
};

const DEFAULT_ITEMS_PER_PAGE = getFormattedItemsPerPageOptions()[1].value;

// Reusable Components
const EmptyState = ({ onClearFilters }: { onClearFilters: () => void }) => (
  <div className='flex flex-col items-center justify-center py-12 px-4'>
    <div className='bg-gray-100 rounded-full p-6 mb-6'>
      <Package className='w-12 h-12 text-gray-400' />
    </div>
    <h3 className='text-xl font-semibold text-gray-900 mb-2'>
      <FormattedMessage
        id='service.itemsNotFound'
        defaultMessage='Услуги не найдены'
      />
    </h3>
    <p className='text-gray-600 text-center max-w-md mb-6'>
      <FormattedMessage
        id='service.tryToClearFilters'
        defaultMessage='По вашему запросу ничего не найдено. Попробуйте изменить параметры поиска или сбросить фильтры.'
      />
    </p>
    <button
      onClick={onClearFilters}
      className='bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors'
    >
      <FormattedMessage
        id='service.clearFilters'
        defaultMessage='Сбросить фильтры'
      />
    </button>
  </div>
);

const LoadingSkeleton = ({ viewMode }: { viewMode: 'grid' | 'list' }) => (
  <div
    className={
      viewMode === 'grid'
        ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
        : 'space-y-4'
    }
  >
    {Array.from({ length: 6 }).map((_, index) =>
      viewMode === 'grid' ? (
        <div
          key={index}
          className='bg-white rounded-lg shadow-sm overflow-hidden'
        >
          <div className='w-full h-48 bg-gray-200 animate-pulse' />
          <div className='p-4 space-y-3'>
            <div className='h-6 bg-gray-200 rounded animate-pulse w-3/4' />
            <div className='h-4 bg-gray-200 rounded animate-pulse w-1/2' />
            <div className='h-4 bg-gray-200 rounded animate-pulse w-1/4' />
            <div className='flex justify-between items-center'>
              <div className='h-4 bg-gray-200 rounded animate-pulse w-1/3' />
              <div className='h-4 bg-gray-200 rounded animate-pulse w-1/4' />
            </div>
          </div>
        </div>
      ) : (
        <div key={index} className='bg-white rounded-lg shadow-sm p-4'>
          <div className='flex gap-4'>
            <div className='w-40 h-40 bg-gray-200 rounded animate-pulse' />
            <div className='flex-1 space-y-3'>
              <div className='h-6 bg-gray-200 rounded animate-pulse w-3/4' />
              <div className='h-4 bg-gray-200 rounded animate-pulse w-1/2' />
              <div className='h-4 bg-gray-200 rounded animate-pulse w-full' />
              <div className='flex justify-between items-center'>
                <div className='h-4 bg-gray-200 rounded animate-pulse w-1/3' />
                <div className='h-4 bg-gray-200 rounded animate-pulse w-1/4' />
              </div>
            </div>
          </div>
        </div>
      ),
    )}
  </div>
);

const ServiceCard = ({
  service,
  viewMode,
  formatMessage,
}: {
  service: Service;
  viewMode: 'grid' | 'list';
  formatMessage: (message: any) => string;
}) => {
  const hasImage = service.images && service.images.length > 0;

  return (
    <div className='bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden'>
      <Link
        to={`/services/${service.id}`}
        className='flex flex-col h-full overflow-hidden'
      >
        {viewMode === 'grid' ? (
          <>
            <div className='w-full bg-gray-100 flex items-center justify-center p-4 md:p-6'>
              {hasImage ? (
                <img
                  src={service.images[0]}
                  alt={service.title}
                  className='w-full max-h-[300px] md:max-h-[400px] object-contain rounded-md shadow'
                />
              ) : (
                <div className='flex items-center justify-center h-full min-h-[300px]'>
                  <Image className='w-12 h-12 text-gray-300' />
                </div>
              )}
            </div>
            <div className='p-4 h-full max-h-full flex flex-col'>
              <div className='flex justify-between items-start mb-2 flex-wrap'>
                <h3 className='text-lg font-semibold mr-4'>{service.title}</h3>
                <span className='text-lg font-bold text-blue-600'>
                  {service.price}RON
                </span>
              </div>
              <div className='flex items-center text-sm text-gray-500 mb-2'>
                <MapPin className='w-4 h-4 mr-1' />
                {formatMessage(locationMessages[`location${service.location}`])}
              </div>
              <div className='flex items-center justify-between mb-3'>
                <span className='text-sm text-gray-600'>
                  {formatMessage(
                    categoryMessages[`category${service.category}`],
                  )}
                </span>
              </div>
              <div className='flex items-center justify-between mt-auto'>
                <button className='text-blue-600 hover:text-blue-700 text-sm font-medium'>
                  <FormattedMessage
                    id='service.details'
                    defaultMessage='Подробнее'
                  />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className='flex flex-col sm:flex-row p-4 gap-4 sm:gap-6'>
            <div className='w-full sm:w-40 h-48 sm:h-40 bg-gray-100 rounded-lg flex items-center justify-center'>
              {hasImage ? (
                <img
                  src={service.images[0]}
                  alt={service.title}
                  className='w-full h-full object-contain rounded-lg'
                />
              ) : (
                <Image className='w-12 h-12 text-gray-300' />
              )}
            </div>
            <div className='flex-1'>
              <div className='flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2'>
                <div>
                  <h3 className='text-xl font-semibold mb-2 sm:mb-1'>
                    {service.title}
                  </h3>
                  <div className='flex items-center text-sm text-gray-500 mb-3 sm:mb-2'>
                    <MapPin className='w-4 h-4 mr-1' />
                    {formatMessage(
                      locationMessages[`location${service.location}`],
                    )}
                  </div>
                </div>
                <span className='text-2xl font-bold text-blue-600 mb-4 sm:mb-0'>
                  {service.price}RON
                </span>
              </div>
              <p className='text-gray-600 mb-4 break-all'>
                {service.description}
              </p>
              <div className='flex items-center justify-center sm:justify-between'>
                <button className='bg-blue-600 text-white px-6 py-3 sm:py-2 rounded-lg hover:bg-blue-700 w-full sm:w-auto'>
                  <FormattedMessage
                    id='service.details'
                    defaultMessage='Подробнее'
                  />
                </button>
              </div>
            </div>
          </div>
        )}
      </Link>
    </div>
  );
};

export function Services() {
  const [searchParams] = useSearchParams();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  // State
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get('category') || CATEGORIES[0].id,
  );
  const [selectedLocation, setSelectedLocation] = useState(
    searchParams.get('location') || LOCATIONS[0].id,
  );
  const [selectedSort, setSelectedSort] = useState(DEFAULT_SORT);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [showFilters, setShowFilters] = useState(
    Boolean(selectedCategory) || false,
  );
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get('query') || '',
  );
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);

  // Debounced values
  const debouncedPriceRange = useDebounce(priceRange, 500);
  const debouncedSearchQuery = useDebounce(searchQuery, 750);

  // Computed values
  const locations = getFormattedLocations(formatMessage);
  const categories = getFormattedCategories(formatMessage);

  const clearFilters = useCallback(() => {
    setSelectedCategory(CATEGORIES[0].id);
    setSelectedLocation(LOCATIONS[0].id);
    setSelectedSort(DEFAULT_SORT);
    setPriceRange({ min: '', max: '' });
    setSearchQuery('');
    setCurrentPage(1);
    navigate('/services', { replace: true });
  }, [navigate]);

  useEffect(() => {
    const isEmpty = searchParams.toString() === '';
    if (isEmpty) {
      clearFilters();
    }
  }, [searchParams, clearFilters]);

  const appliedFilters: Filter[] = useMemo(() => {
    const filters: Filter[] = [];

    // default filter
    filters.push(
      getQueryFilter({
        field: COLLECTIONS_QUERIES.status,
        value: LISTING_ITEM_STATUSES.active,
      }),
    );

    if (selectedLocation !== LOCATIONS[0].id) {
      filters.push(
        getQueryFilter({
          field: COLLECTIONS_QUERIES.location,
          value: selectedLocation,
        }),
      );
    }

    if (selectedCategory !== CATEGORIES[0].id) {
      filters.push(
        getQueryFilter({
          field: COLLECTIONS_QUERIES.category,
          value: selectedCategory,
        }),
      );
    }

    if (debouncedPriceRange.min) {
      filters.push(
        getQueryFilter({
          field: COLLECTIONS_QUERIES.price,
          value: Number(debouncedPriceRange.min),
          operator: '>=',
        }),
      );
    }

    if (debouncedPriceRange.max) {
      filters.push(
        getQueryFilter({
          field: COLLECTIONS_QUERIES.price,
          value: Number(debouncedPriceRange.max),
          operator: '<=',
        }),
      );
    }

    return filters;
  }, [debouncedPriceRange, selectedCategory, selectedLocation]);

  const { data: allItems, isPending } =
    useGetFirestoreItemsByQuery(appliedFilters);

  const filteredItems = useMemo(() => {
    if (!debouncedSearchQuery || !allItems) return allItems;

    return allItems.filter(
      ({ title, description }: Service) =>
        title.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        description.toLowerCase().includes(debouncedSearchQuery.toLowerCase()),
    );
  }, [allItems, debouncedSearchQuery]);

  const sortedItems = useMemo(() => {
    if (!filteredItems) return filteredItems;

    const items = [...filteredItems];

    switch (selectedSort) {
      case 'fromCheapToExpensive':
        return items.sort((a, b) => a?.price - b?.price);
      case 'fromExpensiveToCheap':
        return items.sort((a, b) => b?.price - a?.price);
      case 'byRelevant':
      default:
        return items.sort((a, b) => {
          const dateA = a.updatedAt || a.createdAt || '';
          const dateB = b.updatedAt || b.createdAt || '';
          return dateB.localeCompare(dateA);
        });
    }
  }, [filteredItems, selectedSort]);

  // Pagination
  const totalPages = useMemo(
    () =>
      sortedItems ? Math.ceil(sortedItems.length / Number(itemsPerPage)) : 0,
    [sortedItems, itemsPerPage],
  );
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * Number(itemsPerPage);
    return [...(sortedItems || [])]?.slice(start, start + Number(itemsPerPage));
  }, [sortedItems, currentPage, itemsPerPage]);

  // Handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  return (
    <LayoutWrapper>
      <>
        {/* Search and Filters */}
        <form
          onSubmit={handleSearch}
          className='bg-white rounded-lg p-4 shadow-sm mb-6'
        >
          <div className='flex flex-col md:flex-row gap-4'>
            <div className='flex-1 relative'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' />
              <input
                type='text'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={formatMessage({
                  id: 'search.placeholder',
                  defaultMessage: 'Поиск услуг...',
                })}
                className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              />
            </div>

            <div className='relative'>
              <MapPin className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' />
              <Select
                options={locations}
                placeholder={formatMessage({
                  id: 'addService.locationRequired',
                  defaultMessage: 'Укажите местоположение',
                })}
                onChange={setSelectedLocation}
                value={selectedLocation}
                className='no-padding'
              />
            </div>
            <button
              type='button'
              onClick={() => setShowFilters(!showFilters)}
              className='flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50'
            >
              <SlidersHorizontal className='w-5 h-5 mr-2' />
              <FormattedMessage id='filters.title' defaultMessage='Фильтры' />
            </button>
          </div>

          {showFilters && (
            <div className='mt-4 pt-4 border-t border-gray-200'>
              <div className='flex justify-between items-center mb-4'>
                <h3 className='text-lg font-semibold'>
                  <FormattedMessage
                    id='filters.title'
                    defaultMessage='Фильтры'
                  />
                </h3>
                <button
                  onClick={clearFilters}
                  className='text-sm text-gray-600 hover:text-gray-900 flex items-center'
                >
                  <X className='w-4 h-4 mr-1' />
                  <FormattedMessage
                    id='filters.reset'
                    defaultMessage='Сбросить все'
                  />
                </button>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    <FormattedMessage
                      id='filters.category'
                      defaultMessage='Категория'
                    />
                  </label>
                  <Select
                    options={categories}
                    placeholder={formatMessage({
                      id: 'addService.categoryRequired',
                      defaultMessage: 'Выберите категорию',
                    })}
                    onChange={setSelectedCategory}
                    value={selectedCategory}
                    className='no-padding'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    <FormattedMessage
                      id='filters.price'
                      defaultMessage='Цена'
                    />
                  </label>
                  <div className='flex gap-2'>
                    <input
                      type='number'
                      min='0'
                      value={priceRange.min}
                      onChange={(e) =>
                        setPriceRange((prev) => ({
                          ...prev,
                          min: e.target.value,
                        }))
                      }
                      placeholder={formatMessage({
                        id: 'filters.priceMin',
                        defaultMessage: 'От',
                      })}
                      className='w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    />
                    <input
                      type='number'
                      min='0'
                      value={priceRange.max}
                      onChange={(e) =>
                        setPriceRange((prev) => ({
                          ...prev,
                          max: e.target.value,
                        }))
                      }
                      placeholder={formatMessage({
                        id: 'filters.priceMax',
                        defaultMessage: 'До',
                      })}
                      className='w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    />
                  </div>
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    <FormattedMessage
                      id='filters.sort'
                      defaultMessage='Сортировка'
                    />
                  </label>

                  <Select
                    options={getFormattedSortOptions(formatMessage)}
                    value={selectedSort}
                    onChange={setSelectedSort}
                    className='no-padding'
                  />
                </div>
              </div>
            </div>
          )}
        </form>

        {/* View Controls */}
        <div className='flex justify-between items-center mb-6'>
          <div className='flex items-center space-x-2'>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${
                viewMode === 'grid'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <LayoutGrid className='w-5 h-5' />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <List className='w-5 h-5' />
            </button>
          </div>
          <div className='flex items-center space-x-4'>
            <span className='text-sm text-gray-600'>Показывать по:</span>

            <Select
              options={getFormattedItemsPerPageOptions()}
              value={itemsPerPage}
              onChange={(value) => {
                setItemsPerPage(value);
                setCurrentPage(1);
              }}
              className='no-padding'
            />
          </div>
        </div>

        {/* Content */}
        {isPending ? (
          <LoadingSkeleton viewMode={viewMode} />
        ) : !paginatedItems?.length ? (
          <EmptyState onClearFilters={clearFilters} />
        ) : (
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-4'
            }
          >
            {paginatedItems?.map((service: Service) => (
              <ServiceCard
                key={service.id}
                service={service}
                viewMode={viewMode}
                formatMessage={formatMessage}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && !isPending && (
          <div className='mt-8 flex justify-center items-center space-x-2'>
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronLeft className='w-5 h-5' />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-10 h-10 rounded-lg ${
                  currentPage === page
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`p-2 rounded-lg ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronRight className='w-5 h-5' />
            </button>
          </div>
        )}
      </>
    </LayoutWrapper>
  );
}
